import React, { createContext, useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RegisterDevice } from '../services/auth';
import {
  getStaffUserName,
  getRegisterDevice,
  setIdentificationId,
  setRegisterDevice,
  setPortalServerIp,
  getPortalServerIp,
  setStaffUserName,
  getE2EEncryptionEnabledFlag,
} from '../storage';
import { useToast } from './toast';
import { setPresenceStateLogin } from '../services/presenceState';

import { useIntl } from 'react-intl';
import { SetEncryptionKey } from '../services/e2ee';
import { closeSSEConnection } from '../services/sseManager';

import { clearDecryptedConversationKeyCache } from '../services/conversationKeyCache';
import { setE2EEncryptionEnabled } from '../services/e2eeSettings';
import { clearDecryptedMediaCache } from '../components/balloon/EncryptedMedia';

const AuthContext = createContext();
const AuthProvider = ({ children }) => {
  const intl = useIntl();

  const history = useHistory();
  const [user, setUser] = useState('');
  const [portal, setPortal] = useState(getPortalServerIp());
  const [identificationId, setIdentification] = useState('');
  const { addToast } = useToast();
  const [data, setData] = useState(() => {
    const registerDevice = getRegisterDevice();
    const staffUserName = getStaffUserName();
    if (registerDevice && staffUserName) {
      setIdentificationId({
        IdentificationId:
          registerDevice.RegisterDeviceResult.Configuration.IdentificationId,
      });
      setRegisterDevice({
        RegisterDevice: JSON.stringify(registerDevice),
      });
      setUser(registerDevice.RegisterDeviceResult.Configuration.DisplayName);
      setIdentification(
        registerDevice.RegisterDeviceResult.Configuration.IdentificationId
      );
      setPortalServerIp({
        PortalServerIp:
          registerDevice.RegisterDeviceResult.Configuration.PortalServerIp,
      });
      setPortal(
        registerDevice.RegisterDeviceResult.Configuration.PortalServerIp
      );
      // history.push('/app');
      return { registerDevice };
    }
    return null;
  });

  const signIn = useCallback(
    async ({ userName, password, IpAddress, rsaKeys }) => {
      try {
        const registerDevice = await RegisterDevice({
          userName,
          password,
          IpAddress,
        });
        // console.log(registerDevice);
        if (registerDevice.RegisterDeviceResult.RegistrationRequired) {
          addToast({
            title: intl.formatMessage({ id: 'AuthenticationError' }),
            description: intl.formatMessage({
              id: 'UserNotFoundCheckYourDataAndTryAgain',
            }),
            type: 'error',
          });
        } else {
          setPortalServerIp({
            PortalServerIp:
              registerDevice.RegisterDeviceResult.Configuration.PortalServerIp,
          });

          await setE2EEncryptionEnabled(
            registerDevice.RegisterDeviceResult.Configuration.IdentificationId
          );

          if (rsaKeys && getE2EEncryptionEnabledFlag()) {
            localStorage.setItem(
              `privateRSAKey-${registerDevice.RegisterDeviceResult.Configuration.IdentificationId}`,
              rsaKeys.private
            );

            try {
              await SetEncryptionKey(
                rsaKeys.public,
                registerDevice.RegisterDeviceResult.Configuration
                  .IdentificationId
              );
            } catch (encryptionError) {
              console.error('Encryption key setup failed:', encryptionError);
              return; // prevent further processing if encryption key setup fails
            }
          }

          setPortal(
            registerDevice.RegisterDeviceResult.Configuration.PortalServerIp
          );
          setIdentificationId({
            IdentificationId:
              registerDevice.RegisterDeviceResult.Configuration
                .IdentificationId,
          });
          setRegisterDevice({
            RegisterDevice: JSON.stringify(registerDevice),
          });
          setUser(
            registerDevice.RegisterDeviceResult.Configuration.DisplayName
          );
          setIdentification(
            registerDevice.RegisterDeviceResult.Configuration.IdentificationId
          );
          setStaffUserName(userName);
          history.push('/app');
          setPresenceStateLogin();
        }
      } catch (error) {
        console.error(error);
      }
    },
    [addToast, history]
  );

  const logout = useCallback(() => {
    clearDecryptedMediaCache();
    localStorage.clear();
    sessionStorage.clear();
    setPortal(null);
    closeSSEConnection();
    clearDecryptedConversationKeyCache();
    history.push('/');
  }, [history, setPortal]);

  return (
    <AuthContext.Provider
      value={{
        registerDevice: data,
        user,
        signIn,
        identificationId,
        portal,
        setPortal,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}
export { AuthProvider, AuthContext, useAuth };
